import { request } from '@/api'
import type { TaskKpi } from '@/task-kpis-api'

/** A role's KPI as the portal reads it: a task KPI's shape, owned by a role instead of a task. */
export type RoleKpi = Omit<TaskKpi, 'taskId'> & { roleId: string }

/** What sets a role KPI: the same fields every other layer's KPI carries. */
export type RoleKpiInput = Pick<
    TaskKpi,
    | 'name'
    | 'targetValue'
    | 'targetDirection'
    | 'amberBand'
    | 'onTargetWorseningRule'
    | 'cadenceMinutes'
    | 'servesKpiId'
    | 'sourceKind'
    | 'sourceRef'
>

const kpisOf = (roleId: string) => `/v1/roles/${encodeURIComponent(roleId)}/kpis`
const kpiOf = (roleId: string, kpiId: string) => `${kpisOf(roleId)}/${encodeURIComponent(kpiId)}`

/**
 * A role's KPIs over `/v1/roles/:roleId/kpis` (#901), the same routes the API and the MCP server use. Every write
 * answers with the role's KPIs after it.
 */
export const roleKpisApi = {
    forRole: (roleId: string): Promise<RoleKpi[]> => request<RoleKpi[]>(kpisOf(roleId)),
    define: (roleId: string, input: RoleKpiInput): Promise<RoleKpi[]> =>
        request<RoleKpi[]>(kpisOf(roleId), { method: 'POST', body: JSON.stringify(input) }),
    edit: (roleId: string, kpiId: string, patch: Partial<RoleKpiInput>): Promise<RoleKpi[]> =>
        request<RoleKpi[]>(kpiOf(roleId, kpiId), { method: 'PATCH', body: JSON.stringify(patch) }),
    /** A person signs the KPI off, setting what it is read against as they do. */
    signOff: (roleId: string, kpiId: string, fields: Partial<RoleKpiInput>): Promise<RoleKpi[]> =>
        request<RoleKpi[]>(`${kpiOf(roleId, kpiId)}/sign-off`, { method: 'POST', body: JSON.stringify(fields) }),
    /** Takes the KPI off the role; its readings stay. */
    withdraw: (roleId: string, kpiId: string): Promise<RoleKpi[]> =>
        request<RoleKpi[]>(kpiOf(roleId, kpiId), { method: 'DELETE' }),
}
